import Column from '@amzn/meridian/column';
import Toggle from '@amzn/meridian/toggle';
import { Field, FormikProps, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import {
  CoachingFormData,
  CoachingSessionInput,
} from '../../../api/CoachingForm/schema';
import ConditionalTooltip from '../FieldComponents/ConditionalTooltip';
import ErrorMessage from '../FieldComponents/ErrorMessage';
import { getLabelFromValue } from '../FieldComponents/Helpers';
import Select from '../FieldComponents/Select';
import Textarea from '../FieldComponents/Textarea';
import { DisplayObject } from '../LayoutComponents/FieldArrayDisplay';
import {
  getAllDurations,
  getAllMethods,
  getAllPreparationTimes,
  getAllTactics,
  getDurations,
  getMethods,
  getPreparationTimes,
  getTactics,
} from './Data';
import { COACHING_TEXT_LIMIT } from '../../../Util';

export const convertSessionToDisplay = (
  session: CoachingSessionInput,
  intl: IntlShape,
  showManagerFields: boolean
): DisplayObject[] => {
  const {
    managerNotes,
    coachingTactic,
    coachingMethod,
    duration,
    preparationTime,
    coachingSummaryNotes,
  } = session;

  const noInfoEntered = intl.formatMessage({
    id: 'cs_perfportal_no_information_entered',
    defaultMessage: 'No Information entered',
  });

  const managerNoteDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_manager_notes',
      defaultMessage: 'Manager notes:',
    }),
    value: managerNotes.length > 0 ? managerNotes : noInfoEntered,
  };

  const tacticDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_coaching_tactic',
      defaultMessage: 'Coaching tactic:',
    }),
    value:
      coachingTactic !== null
        ? getLabelFromValue(coachingTactic, getAllTactics(intl))
        : noInfoEntered,
  };

  const methodDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_coaching_method',
      defaultMessage: 'Coaching method:',
    }),
    value:
      coachingMethod !== null
        ? getLabelFromValue(coachingMethod, getAllMethods(intl))
        : noInfoEntered,
  };

  const durationDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_coaching_duration',
      defaultMessage: 'Duration:',
    }),
    value:
      typeof duration === 'string'
        ? getLabelFromValue(duration, getAllDurations())
        : noInfoEntered,
  };

  const preparationDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_preparation_time',
      defaultMessage: 'Preparation time:',
    }),
    value:
      typeof preparationTime === 'string'
        ? getLabelFromValue(preparationTime, getAllPreparationTimes(intl))
        : noInfoEntered,
  };

  const summaryNoteDisplay = {
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_label_readonly_summary_notes',
      defaultMessage: 'Coaching summary notes:',
    }),
    value:
      coachingSummaryNotes.length > 0 ? coachingSummaryNotes : noInfoEntered,
  };

  return showManagerFields
    ? [
        managerNoteDisplay,
        tacticDisplay,
        methodDisplay,
        durationDisplay,
        preparationDisplay,
        summaryNoteDisplay,
      ]
    : [methodDisplay, durationDisplay, summaryNoteDisplay];
};

export const FormPanel: React.FC = () => {
  const { values, errors, touched }: FormikProps<CoachingFormData> =
    useFormikContext();
  const intl = useIntl();
  const coachingSessionTouched = touched.coachingSession || {};
  const coachingSessionErrors = errors.coachingSession || {};

  const [hideManagerFields, setHideManagerFields] = useState(false);
  const { coachingSummaryNotes: summaryError, ...hiddenErrors } =
    coachingSessionErrors;
  const { coachingSummaryNotes: summaryTouch, ...hiddenTouch } =
    coachingSessionTouched;
  const hasHiddenErrors =
    hideManagerFields &&
    Object.values(hiddenErrors).filter(a => !!a).length > 0;
  const hasHiddenTouched =
    hideManagerFields && Object.values(hiddenTouch).filter(a => !!a).length > 0;

  const isNotTacticSelected = values.coachingSession.coachingTactic === null;
  const isNotTacticSelectedMessage = intl.formatMessage({
    id: 'cs_perfportal_coaching_session_method_disabled_tactic_required',
    defaultMessage:
      'You must select a coaching tactic before selecting a coaching method',
    description: 'Coaching Method Tooltip',
  });

  return (
    <Column spacing='400'>
      <Toggle
        data-csa-c-type='element'
        data-csa-c-slot-id='coaching-form-coaching-session-toggle-button-slot'
        data-csa-c-content-id='coaching-form-coaching-session-toggle-button'
        checked={hideManagerFields}
        onChange={setHideManagerFields}
      >
        <FormattedMessage
          id='cs_perfportal_coaching_session_hide_manager_fields'
          defaultMessage='Hide manager fields'
        />
      </Toggle>
      {hasHiddenErrors && hasHiddenTouched && (
        <ErrorMessage
          message={intl.formatMessage({
            id: 'cs_perfportal_coaching_session_show_fields_for_errors',
            defaultMessage: 'Show manager fields to correct errors',
          })}
        />
      )}
      {!hideManagerFields && (
        <>
          <Field
            data-cy='coaching-session-manager-notes'
            as={Textarea}
            name='coachingSession.managerNotes'
            label={intl.formatMessage({
              id: 'cs_perfportal_coaching_session_label_optional_manager_notes',
              defaultMessage: 'Manager notes (optional)',
              description: 'Coaching Session Optional Manager Notes',
            })}
            maxLength={COACHING_TEXT_LIMIT}
            errorText={
              coachingSessionTouched.managerNotes &&
              coachingSessionErrors.managerNotes
            }
          />
          <Field
            data-cy='coaching-session-coaching-tactic'
            as={Select}
            name='coachingSession.coachingTactic'
            label={intl.formatMessage({
              id: 'cs_perfportal_coaching_session_label_required_coaching_tactic',
              defaultMessage: 'Coaching tactic *',
              description: 'Coaching Session Coaching Tactic Selection',
            })}
            options={getTactics(intl)}
            search
            errorText={
              coachingSessionTouched.coachingTactic &&
              coachingSessionErrors.coachingTactic
            }
          />
          <ConditionalTooltip
            tooltip={isNotTacticSelected && isNotTacticSelectedMessage}
          >
            <Field
              data-cy='coaching-session-coaching-method'
              as={Select}
              name='coachingSession.coachingMethod'
              label={intl.formatMessage({
                id: 'cs_perfportal_coaching_session_label_required_coaching_method',
                defaultMessage: 'Coaching method *',
                description: 'Coaching Session Coaching Tactic Selection',
              })}
              options={getMethods(intl)}
              search
              errorText={
                coachingSessionTouched.coachingMethod &&
                coachingSessionErrors.coachingMethod
              }
              disabled={isNotTacticSelected}
            />
          </ConditionalTooltip>
          <Field
            data-cy='coaching-session-duration'
            as={Select}
            name='coachingSession.duration'
            label={intl.formatMessage({
              id: 'cs_perfportal_coaching_session_label_required_coaching_duration',
              defaultMessage: 'Duration *',
              description: 'Coaching Session Duration',
            })}
            options={getDurations()}
            search
            errorText={
              coachingSessionTouched.duration && coachingSessionErrors.duration
            }
          />
          <Field
            data-cy='coaching-session-prep-time'
            as={Select}
            name='coachingSession.preparationTime'
            label={intl.formatMessage({
              id: 'cs_perfportal_coaching_session_label_required_preparation_time',
              defaultMessage: 'Preparation time *',
              description: 'Coaching Session Preparation Time',
            })}
            options={getPreparationTimes(intl)}
            search
            errorText={
              coachingSessionTouched.preparationTime &&
              coachingSessionErrors.preparationTime
            }
          />
        </>
      )}
      <Field
        data-cy='coaching-session-coaching-summary-notes'
        as={Textarea}
        name='coachingSession.coachingSummaryNotes'
        label={intl.formatMessage({
          id: 'cs_perfportal_coaching_session_label_optional_summary_notes',
          defaultMessage: 'Coaching summary notes (optional)',
          description: 'Coaching Session Optional Summary Notes',
        })}
        maxLength={COACHING_TEXT_LIMIT}
        errorText={
          coachingSessionTouched.coachingSummaryNotes &&
          coachingSessionErrors.coachingSummaryNotes
        }
      />
    </Column>
  );
};
